import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardWrapper from '../components/DashboardWrapper';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import FollowingProductsTable from '../components/FollowingProductsTable';
import FavoritesProductsTable from '../components/FavoritesProductsTable';
import FavoriteListsManager from '../components/FavoriteListsManager';
import ReviewsProductsTable from '../components/ReviewsProductsTable';
import { computeProfileCompletion, validators, USER_FIELDS } from '../lib/userValidation';
import AutoFilledField from '../components/AutoFilledField';
import OrdersContent from '../components/OrdersContent';
import useNavigationGuard from '../hooks/useNavigationGuard';
import NavigationBlockerDialog from '../components/NavigationBlockerDialog';
import { 
  User, 
  Store, 
  Heart, 
  Flag, 
  MessageSquare, 
  Mail, 
  Phone, 
  Calendar,
  MapPin,
  Building2,
  Users,
  Euro,
  Globe,
  AlertCircle,
  CheckCircle2,
  FileText
} from 'lucide-react';

/**
 * ProfilePage V2 - Refonte complète "Mon Compte"
 * 
 * STRUCTURE:
 * - Onglet 1: Informations du compte (personnelles + magasin)
 * - Onglet 2: Favoris
 * - Onglet 3: Produits à suivre
 * - Onglet 4: Avis et commentaires
 * 
 * VALIDATIONS:
 * - SIREN: exactement 9 chiffres
 * - Dates: format ISO 8601
 * - site_ecommerce: booléen (Oui/Non)
 * 
 * CONVENTIONS:
 * - snake_case pour tous les champs
 * - Champs obligatoires marqués avec astérisque rouge
 * - Responsive mobile-first
 */

// Composant pour label avec indicateur obligatoire
const RequiredLabel = ({ htmlFor, children }) => (
  <Label htmlFor={htmlFor} className="flex items-center gap-1">
    {children}
    <span className="text-red-500 text-sm">*</span>
  </Label>
);

// Composant pour afficher une date ISO en format lisible
const formatDateDisplay = (isoDate) => {
  if (!isoDate) return '';
  try {
    const date = new Date(isoDate);
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch { return isoDate; }
};

// Réutilisation des validateurs de la source commune
const validateSiret = validators.siret;
const validateEmail = validators.email;

// Helper local: conversion booléen vers string pour Select
const boolToSelectValue = (value) => {
  if (value === true) return 'true';
  if (value === false) return 'false';
  return '';
};

const ProfilePage = () => {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'account');
  
  // Complétion du profil (calculée à partir des données réelles)
  const completion = useMemo(() => computeProfileCompletion(user), [user]);
  
  // V5: Field metadata (provenance & confirmation)
  const fieldMetadata = useMemo(() => user?.field_metadata || {}, [user]);
  const pendingFields = useMemo(() => 
    Object.entries(fieldMetadata).filter(([_, m]) => m.status === 'pending').map(([f]) => f),
    [fieldMetadata]
  );
  
  const handleConfirmField = async (fieldName) => {
    try {
      const res = await api.post('/users/field-confirm', { field: fieldName });
      // Mettre à jour le user local avec la metadata confirmée
      const updatedMeta = { ...fieldMetadata, [fieldName]: { ...fieldMetadata[fieldName], status: 'confirmed' } };
      const updatedUser = { ...user, field_metadata: updatedMeta };
      if (res.data.account_status_transition) {
        updatedUser.account_status = res.data.account_status_transition;
      }
      updateUser(updatedUser);
      toast.success(`${fieldName === 'pseudo' ? 'Pseudo' : 'Information'} confirmé(e)`);
    } catch (err) {
      toast.error('Erreur lors de la confirmation');
    }
  };
  
  const [formData, setFormData] = useState({
    // Informations personnelles
    pseudo: '', // Pseudo affiché publiquement — obligatoire pour profil complet
    email: '', // Email professionnel
    prenom_utilisateur: '',
    nom_utilisateur: '',
    telephone: '',
    date_naissance: '',
    // Informations magasin
    nom_magasin: '',
    raison_sociale: '', // V4: distincte du nom commercial
    siret: '',
    adresse_magasin: '',
    code_postal: '',
    ville: '',
    date_creation_magasin: '',
    surface_magasin: '',
    ca_magasin: '',
    nb_salaries: '',
    // Caractéristiques magasin (zone_implantation déplacée ici V4)
    zone_implantation: '',
    jeu_libre: null,
    animations_magasin: null,
    site_ecommerce: null,
    capacite_accueil: '',
    // Préférences
    newsletter: false,
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirtyRaw] = useState(false);
  const formReadyRef = useRef(false);
  const _setIsDirty = (val) => {
    // Ignorer les changements automatiques avant que le formulaire soit prêt
    if (val === true && !formReadyRef.current) return;
    setIsDirtyRaw(val);
  };
  const [originalFormData, setOriginalFormData] = useState(null);
  const { blocker } = useNavigationGuard(isDirty);
  const [favorites, setFavorites] = useState([]);
  const [watchlist, setWatchlist] = useState([]);
  const [comments, setComments] = useState([]);
  const [loadingData, setLoadingData] = useState(true);
  
  // État pour les listes de favoris
  const [favoriteLists, setFavoriteLists] = useState([]);
  const [selectedListId, setSelectedListId] = useState(null);
  const [filteredFavorites, setFilteredFavorites] = useState([]);

  // ════════════════════════════════════════════════════════════════════════════
  // CONFIGURATION LOCALE - Onglets selon rôle (pas de flags globaux)
  // ════════════════════════════════════════════════════════════════════════════
  
  const isPartner = user?.role === 'partenaire';
  
  // Config des onglets - objet local simple
  const tabsConfig = isPartner 
    ? [
        { id: 'account', label: 'Informations', icon: User, showBadge: false }
      ]
    : [
        { id: 'account', label: 'Informations', icon: User, showBadge: false },
        { id: 'orders', label: 'Mes Commandes', icon: FileText },
        { id: 'favorites', label: 'Wishlist', icon: Heart, badge: favorites.length },
        { id: 'watchlist', label: 'À suivre', icon: Flag, badge: watchlist.length },
        { id: 'comments', label: 'Avis', icon: MessageSquare, badge: comments.length }
      ];

  // Charger les données utilisateur
  useEffect(() => {
    if (user) {
      const str = (v) => (v != null && v !== '') ? String(v) : '';
      const loaded = {
        pseudo: user.pseudo || '',
        email: user.email || '',
        prenom_utilisateur: user.prenom_utilisateur || '',
        nom_utilisateur: user.nom_utilisateur || '',
        telephone: user.telephone || '',
        date_naissance: user.date_naissance || '',
        nom_magasin: user.nom_magasin || '',
        raison_sociale: user.raison_sociale || '',
        siret: user.siret || '',
        adresse_magasin: user.adresse_magasin || '',
        code_postal: user.code_postal || '',
        ville: user.ville || '',
        date_creation_magasin: user.date_creation_magasin || '',
        surface_magasin: str(user.surface_magasin),
        ca_magasin: str(user.ca_magasin),
        nb_salaries: str(user.nb_salaries),
        zone_implantation: user.zone_implantation || '',
        jeu_libre: user.jeu_libre ?? null,
        animations_magasin: user.animations_magasin ?? null,
        site_ecommerce: user.site_ecommerce ?? null,
        capacite_accueil: str(user.capacite_accueil),
        newsletter: user.newsletter || false,
      };
      setFormData(loaded);
      setOriginalFormData(loaded);
      // Activer le suivi dirty APRÈS le premier rendu avec les données chargées
      // Délai pour laisser les composants Select/Radio se stabiliser
      formReadyRef.current = false;
      setTimeout(() => { formReadyRef.current = true; }, 500);
    }
  }, [user]);

  // Detect dirty state — marqué dirty uniquement par interaction utilisateur
  // (pas par comparaison JSON qui est fragile avec les types numériques/booléens)

  // Charger favoris, watchlist et commentaires
  useEffect(() => {
    const loadUserData = async () => {
      setLoadingData(true);
      try {
        const [favsRes, watchRes, commentsRes, listsRes] = await Promise.all([
          api.get('/favorites?type=favori').catch(() => ({ data: [] })),
          api.get('/favorites?type=a_suivre').catch(() => ({ data: [] })),
          api.get('/comments/user').catch(() => ({ data: [] })),
          api.get('/favorite-lists').catch(() => ({ data: [] }))
        ]);
        setFavorites(Array.isArray(favsRes.data) ? favsRes.data : []);
        setWatchlist(Array.isArray(watchRes.data) ? watchRes.data : []);
        setFavoriteLists(Array.isArray(listsRes.data) ? listsRes.data : []);
        // Handle both array response and {comments: []} response from backend
        const commentsData = commentsRes.data;
        if (Array.isArray(commentsData)) {
          setComments(commentsData);
        } else if (commentsData && Array.isArray(commentsData.comments)) {
          setComments(commentsData.comments);
        } else {
          setComments([]);
        }
      } catch (error) {
        console.error('Error loading user data:', error);
      } finally {
        setLoadingData(false);
      }
    };
    
    if (user) {
      loadUserData();
    }
  }, [user]);

  // Filtrer les favoris par liste sélectionnée
  useEffect(() => {
    if (selectedListId) {
      // Filtrer par list_ids
      const filtered = favorites.filter(fav => 
        fav.list_ids && fav.list_ids.includes(selectedListId)
      );
      setFilteredFavorites(filtered);
    } else {
      setFilteredFavorites(favorites);
    }
  }, [favorites, selectedListId]);

  // Callback pour mise à jour de commentaire favori
  // Cross-sync: met aussi à jour le même produit dans watchlist
  const handleFavoriteCommentUpdate = useCallback((favoriteId, newComment) => {
    // Trouver le product_id du favori modifié
    const updatedFav = favorites.find(f => f.id === favoriteId);
    const productId = updatedFav?.product_id;
    
    setFavorites(prev => prev.map(fav => 
      fav.id === favoriteId ? { ...fav, commentaire: newComment } : fav
    ));
    
    // Cross-sync: propager dans watchlist (même product_id)
    if (productId) {
      setWatchlist(prev => prev.map(fav => 
        fav.product_id === productId ? { ...fav, commentaire: newComment } : fav
      ));
    }
  }, [favorites]);

  // Callback pour mise à jour de commentaire produit suivi (à suivre)
  // Cross-sync: met aussi à jour le même produit dans favorites
  const handleFollowingCommentUpdate = useCallback((favoriteId, newComment) => {
    // Trouver le product_id du suivi modifié
    const updatedFollow = watchlist.find(f => f.id === favoriteId);
    const productId = updatedFollow?.product_id;
    
    setWatchlist(prev => prev.map(fav => 
      fav.id === favoriteId ? { ...fav, commentaire: newComment } : fav
    ));
    
    // Cross-sync: propager dans favorites (même product_id)
    if (productId) {
      setFavorites(prev => prev.map(fav => 
        fav.product_id === productId ? { ...fav, commentaire: newComment } : fav
      ));
    }
  }, [watchlist]);

  // Callback pour suppression de favori
  const handleRemoveFavorite = useCallback(async (favoriteId, productId) => {
    try {
      await api.delete(`/favorites/${favoriteId}`);
      setFavorites(prev => prev.filter(fav => fav.id !== favoriteId));
      toast.success('Produit retiré de la Wishlist');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  }, []);

  // Callback pour recharger les listes
  const handleListsChange = useCallback(async () => {
    try {
      const [favsRes, listsRes] = await Promise.all([
        api.get('/favorites?type=favori'),
        api.get('/favorite-lists')
      ]);
      setFavorites(Array.isArray(favsRes.data) ? favsRes.data : []);
      setFavoriteLists(Array.isArray(listsRes.data) ? listsRes.data : []);
    } catch (error) {
      console.error('Error reloading lists:', error);
    }
  }, []);

  // Callback pour mise à jour des listes d'un favori (sans reload complet)
  const handleFavoriteListsUpdate = useCallback((favoriteId, newListIds) => {
    setFavorites(prev => prev.map(fav => 
      fav.id === favoriteId ? { ...fav, list_ids: newListIds } : fav
    ));
    // Aussi recharger les listes pour mettre à jour les compteurs
    handleListsChange();
  }, [handleListsChange]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    setFormData(prev => ({ ...prev, [name]: newValue }));
    // Marquer dirty uniquement si la valeur diffère de l'original
    if (originalFormData && originalFormData[name] !== newValue) {
      _setIsDirty(true);
    }
    
    // Validation en temps réel pour SIRET
    if (name === 'siret') {
      const validation = validateSiret(value);
      setErrors(prev => ({
        ...prev,
        siret: validation.valid ? '' : validation.message
      }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (originalFormData && originalFormData[name] !== value) {
      _setIsDirty(true);
    }
  };

  const handleBooleanChange = (name, value) => {
    // Valeur vide (radio non sélectionné) → ne pas changer l'état
    if (value === '' || value === undefined) return;
    const boolVal = value === 'true';
    setFormData(prev => ({ ...prev, [name]: boolVal }));
    if (originalFormData && originalFormData[name] !== boolVal) {
      _setIsDirty(true);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation avant soumission
    const newErrors = {};
    
    // Validation Prénom et Nom (obligatoires)
    if (!formData.prenom_utilisateur || !formData.prenom_utilisateur.trim()) {
      newErrors.prenom_utilisateur = 'Le prénom est obligatoire';
    }
    
    if (!formData.nom_utilisateur || !formData.nom_utilisateur.trim()) {
      newErrors.nom_utilisateur = 'Le nom de famille est obligatoire';
    }
    
    // Validation Email
    if (formData.email) {
      const emailValidation = validateEmail(formData.email);
      if (!emailValidation.valid) {
        newErrors.email = emailValidation.message;
      }
    }
    
    // Validation SIREN
    if (formData.siret) {
      const siretValidation = validateSiret(formData.siret);
      if (!siretValidation.valid) {
        newErrors.siret = siretValidation.message;
      }
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Veuillez corriger les erreurs avant de soumettre');
      return;
    }
    
    setLoading(true);
    setErrors({});

    try {
      const submitData = {
        ...formData,
        surface_magasin: formData.surface_magasin ? parseInt(formData.surface_magasin) : null,
        ca_magasin: formData.ca_magasin ? parseFloat(formData.ca_magasin) : null,
        nb_salaries: formData.nb_salaries ? parseInt(formData.nb_salaries) : null,
        jeu_libre: formData.jeu_libre,
        animations_magasin: formData.animations_magasin,
        capacite_accueil: formData.capacite_accueil ? parseInt(formData.capacite_accueil) : null,
        // Nettoyer le SIRET
        siret: formData.siret ? formData.siret.replace(/[\s\-]/g, '') : null,
      };

      const response = await api.put('/users/profile', submitData);
      updateUser(response.data);
      setOriginalFormData({...formData});
      _setIsDirty(false); // Reset dirty state après sauvegarde
      toast.success('Profil mis à jour avec succès !');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (detail) {
        toast.error(detail);
        // Parser les erreurs de validation backend
        if (detail.includes('SIRET')) {
          setErrors(prev => ({ ...prev, siret: detail }));
        }
        if (detail.includes('email') || detail.includes('Email')) {
          setErrors(prev => ({ ...prev, email: detail }));
        }
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardWrapper>
      <div 
        className="profile-page-container"
        data-testid="profile-page"
        style={{
          maxWidth: '1200px',
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingLeft: '16px',
          paddingRight: '16px',
          paddingTop: '32px',
          paddingBottom: '32px',
          width: '100%',
          boxSizing: 'border-box'
        }}
      >
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold" data-testid="profile-title">
            Mon Compte
          </h1>
          <p className="text-muted-foreground mt-2">
            Gérez vos informations personnelles et votre magasin
          </p>
          {(user?.pseudo || user?.email) && (
            <div className="flex flex-wrap items-center gap-2 mt-3 text-sm text-muted-foreground">
              {user?.pseudo && (
                <span className="font-medium text-foreground" data-testid="profile-pseudo-display">
                  {user.pseudo}
                </span>
              )}
              {user?.pseudo && user?.email && <span>•</span>}
              {user?.email && (
                <>
                  <Mail className="h-4 w-4" />
                  <span data-testid="profile-email-display">{user.email}</span>
                </>
              )}
              {(() => {
                const status = user.account_status;
                if (status === 'valide' || (!status && user.is_validated)) return (
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200" data-testid="profile-status-badge">
                    <CheckCircle2 className="h-3 w-3 mr-1" />
                    Validé
                  </Badge>
                );
                if (status === 'a_valider') return (
                  <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200" data-testid="profile-status-badge">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    À valider
                  </Badge>
                );
                if (status === 'a_completer') return (
                  <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200" data-testid="profile-status-badge">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Profil incomplet
                  </Badge>
                );
                if (status === 'email_pending') return (
                  <Badge variant="outline" className="bg-gray-50 text-gray-600 border-gray-200" data-testid="profile-status-badge">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Email non vérifié
                  </Badge>
                );
                if (status === 'refuse') return (
                  <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200" data-testid="profile-status-badge">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Refusé
                  </Badge>
                );
                return null;
              })()}
            </div>
          )}
        </div>

        {/* Bloc unifié: Profil incomplet (statut + texte + progression + pending) */}
        {!isPartner && user?.account_status === 'a_completer' && (
          <div className="mb-6 p-4 sm:p-5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="profile-incomplete-block">
            <h3 className="text-sm font-semibold text-amber-900 mb-2">Profil incomplet</h3>
            <p className="text-sm text-amber-800 leading-relaxed">
              Votre profil est incomplet. Vous pouvez dès maintenant naviguer sur la plateforme, avec un accès limité.
              Pour accéder aux conditions de distribution, complétez les champs de votre profil signalés par une étoile
              rouge (<span className="text-red-500 font-bold">*</span>).
            </p>

            <div className="mt-3">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-sm font-medium text-amber-800">
                  {completion.filled} / {completion.total} informations complétées
                </span>
              </div>
              <div className="w-full h-2 bg-amber-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-amber-500 rounded-full transition-all duration-500"
                  style={{ width: `${completion.percent}%` }}
                  role="progressbar"
                  aria-valuenow={completion.filled}
                  aria-valuemin={0}
                  aria-valuemax={completion.total}
                  aria-label={`${completion.filled} sur ${completion.total} informations complétées`}
                  data-testid="profile-completion-bar"
                />
              </div>
            </div>

            {pendingFields.length > 0 && (
              <p className="text-xs text-blue-700 mt-3" data-testid="pending-fields-hint">
                {pendingFields.length} information{pendingFields.length > 1 ? 's' : ''} préremplie{pendingFields.length > 1 ? 's' : ''} à vérifier.
              </p>
            )}
          </div>
        )}

        {/* Tabs Navigation - Rendu basé sur config locale */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className={`grid w-full mb-6 ${isPartner ? 'grid-cols-1' : 'grid-cols-2 md:grid-cols-5'}`}>
            {tabsConfig.map(tab => {
              const IconComponent = tab.icon;
              return (
                <TabsTrigger 
                  key={tab.id}
                  value={tab.id} 
                  className="flex items-center gap-2" 
                  data-testid={`tab-${tab.id}`}
                >
                  <IconComponent className="h-4 w-4" />
                  <span className={isPartner ? '' : 'hidden sm:inline'}>{tab.label}</span>
                  {tab.badge > 0 && (
                    <Badge variant="secondary" className="ml-1">{tab.badge}</Badge>
                  )}
                </TabsTrigger>
              );
            })}
          </TabsList>

          {/* === ONGLET 1: INFORMATIONS DU COMPTE === */}
          <TabsContent value="account">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Section: Informations personnelles */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Informations personnelles
                  </CardTitle>
                  <CardDescription>
                    Vos informations de contact
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Pseudo - obligatoire pour profil complet */}
                  <AutoFilledField fieldName="pseudo" meta={fieldMetadata.pseudo} onConfirm={handleConfirmField} label="le pseudo">
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="pseudo">Pseudo</RequiredLabel>
                    <Input
                      id="pseudo"
                      name="pseudo"
                      value={formData.pseudo}
                      onChange={handleChange}
                      placeholder="MonPseudo123"
                      data-testid="profile-pseudo-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Ce pseudo sera utilisé pour vous identifier sur la plateforme, notamment lorsque vous publierez une intervention ou laisserez un avis.
                    </p>
                  </div>
                  </AutoFilledField>
                  {/* Ancien hint pseudo - remplacé par AutoFilledField */}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="prenom_utilisateur">Prénom</RequiredLabel>
                      <Input
                        id="prenom_utilisateur"
                        name="prenom_utilisateur"
                        value={formData.prenom_utilisateur}
                        onChange={handleChange}
                        placeholder="Votre prénom"
                        className={errors.prenom_utilisateur ? 'border-red-500' : ''}
                        data-testid="profile-prenom-input"
                      />
                      {errors.prenom_utilisateur && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.prenom_utilisateur}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <RequiredLabel htmlFor="nom_utilisateur">Nom de famille</RequiredLabel>
                      <Input
                        id="nom_utilisateur"
                        name="nom_utilisateur"
                        value={formData.nom_utilisateur}
                        onChange={handleChange}
                        placeholder="Votre nom"
                        className={errors.nom_utilisateur ? 'border-red-500' : ''}
                        data-testid="profile-nom-input"
                      />
                      {errors.nom_utilisateur && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.nom_utilisateur}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Email MODIFIABLE */}
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="email">Email professionnel</RequiredLabel>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="votre@email.com"
                      className={errors.email ? 'border-red-500' : ''}
                      data-testid="profile-email-input"
                    />
                    {errors.email && (
                      <p className="text-xs text-red-500 flex items-center gap-1">
                        <AlertCircle className="h-3 w-3" />
                        {errors.email}
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="telephone" className="flex items-center gap-2">
                        <Phone className="h-4 w-4" />
                        Téléphone
                      </Label>
                      <Input
                        id="telephone"
                        name="telephone"
                        type="tel"
                        value={formData.telephone}
                        onChange={handleChange}
                        placeholder="06 12 34 56 78"
                        data-testid="profile-telephone-input"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="date_naissance" className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Date de naissance
                      </Label>
                      <Input
                        id="date_naissance"
                        name="date_naissance"
                        type="date"
                        value={formData.date_naissance}
                        onChange={handleChange}
                        data-testid="profile-date-naissance-input"
                      />
                      {formData.date_naissance && (
                        <p className="text-xs text-muted-foreground">
                          {formatDateDisplay(formData.date_naissance)}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Section: Informations magasin - UNIQUEMENT POUR RETAILERS */}
              {user?.role !== 'partenaire' && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Store className="h-5 w-5" />
                    Informations du magasin
                  </CardTitle>
                  <CardDescription>
                    Les informations de votre point de vente
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="nom_magasin">Nom du magasin</RequiredLabel>
                      <Input
                        id="nom_magasin"
                        name="nom_magasin"
                        value={formData.nom_magasin}
                        onChange={handleChange}
                        placeholder="Ma Boutique de Jeux"
                        data-testid="profile-nom-magasin-input"
                      />
                    </div>

                    <AutoFilledField fieldName="raison_sociale" meta={fieldMetadata.raison_sociale} onConfirm={handleConfirmField} label="la raison sociale">
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="raison_sociale">Raison sociale</RequiredLabel>
                      <Input
                        id="raison_sociale"
                        name="raison_sociale"
                        value={formData.raison_sociale}
                        onChange={handleChange}
                        placeholder="SARL DUPONT JEUX"
                        data-testid="profile-raison-sociale-input"
                      />
                    </div>
                    </AutoFilledField>

                    <div className="space-y-2">
                      <RequiredLabel htmlFor="siret">Numéro SIRET</RequiredLabel>
                      <Input
                        id="siret"
                        name="siret"
                        value={formData.siret}
                        onChange={handleChange}
                        placeholder="14 chiffres"
                        maxLength={14}
                        inputMode="numeric"
                        className={errors.siret ? 'border-red-500' : ''}
                        data-testid="profile-siret-input"
                      />
                      {errors.siret && (
                        <p className="text-xs text-red-500 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.siret}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        14 chiffres, sans espaces ni tirets
                      </p>
                    </div>
                  </div>

                  <AutoFilledField fieldName="adresse_magasin" meta={fieldMetadata.adresse_magasin} onConfirm={handleConfirmField} label="l'adresse">
                  <div className="space-y-2">
                    <RequiredLabel htmlFor="adresse_magasin">
                      <MapPin className="h-4 w-4" />
                      Adresse
                    </RequiredLabel>
                    <Input
                      id="adresse_magasin"
                      name="adresse_magasin"
                      value={formData.adresse_magasin}
                      onChange={handleChange}
                      placeholder="123 Rue des Jeux"
                      data-testid="profile-adresse-input"
                    />
                  </div>
                  </AutoFilledField>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutoFilledField fieldName="code_postal" meta={fieldMetadata.code_postal} onConfirm={handleConfirmField} label="le code postal">
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="code_postal">Code postal</RequiredLabel>
                      <Input
                        id="code_postal"
                        name="code_postal"
                        value={formData.code_postal}
                        onChange={handleChange}
                        placeholder="75001"
                        maxLength={5}
                        data-testid="profile-code-postal-input"
                      />
                    </div>
                    </AutoFilledField>

                    <AutoFilledField fieldName="ville" meta={fieldMetadata.ville} onConfirm={handleConfirmField} label="la ville">
                    <div className="space-y-2">
                      <RequiredLabel htmlFor="ville">Ville</RequiredLabel>
                      <Input
                        id="ville"
                        name="ville"
                        value={formData.ville}
                        onChange={handleChange}
                        placeholder="Paris"
                        data-testid="profile-ville-input"
                      />
                    </div>
                    </AutoFilledField>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <AutoFilledField fieldName="date_creation_magasin" meta={fieldMetadata.date_creation_magasin} onConfirm={handleConfirmField} label="la date de création">
                    <div className="space-y-2">
                      <Label htmlFor="date_creation_magasin" className="flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        Date de création
                      </Label>
                      <Input
                        id="date_creation_magasin"
                        name="date_creation_magasin"
                        type="date"
                        value={formData.date_creation_magasin}
                        onChange={handleChange}
                        data-testid="profile-date-creation-input"
                      />
                      {formData.date_creation_magasin && (
                        <p className="text-xs text-muted-foreground">
                          {formatDateDisplay(formData.date_creation_magasin)}
                        </p>
                      )}
                    </div>
                    </AutoFilledField>
                  </div>

                  {/* Caractéristiques du magasin */}
                  <div className="border-t pt-4 mt-4">
                    <h4 className="text-sm font-semibold mb-3 flex items-center gap-2">
                      <Building2 className="h-4 w-4" />
                      Caractéristiques
                    </h4>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Zone d'implantation — V4 déplacée depuis Informations magasin */}
                      <div className="space-y-2">
                        <Label htmlFor="zone_implantation">Zone d'implantation</Label>
                        <Select
                          value={formData.zone_implantation}
                          onValueChange={(value) => handleSelectChange('zone_implantation', value)}
                        >
                          <SelectTrigger data-testid="profile-zone-select">
                            <SelectValue placeholder="Sélectionnez" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="centre ville">Centre ville</SelectItem>
                            <SelectItem value="périférie">Périphérie</SelectItem>
                            <SelectItem value="centre commercial">Centre commercial</SelectItem>
                            <SelectItem value="zone rural">Zone rurale</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="surface_magasin">Surface (m²)</Label>
                        <Input
                          id="surface_magasin"
                          name="surface_magasin"
                          type="number"
                          min="0"
                          value={formData.surface_magasin}
                          onChange={handleChange}
                          placeholder="100"
                          data-testid="profile-surface-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="nb_salaries" className="flex items-center gap-2">
                          <Users className="h-4 w-4" />
                          Salariés
                        </Label>
                        <Input
                          id="nb_salaries"
                          name="nb_salaries"
                          type="number"
                          min="0"
                          value={formData.nb_salaries}
                          onChange={handleChange}
                          placeholder="2"
                          data-testid="profile-nb-salaries-input"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="ca_magasin" className="flex items-center gap-2">
                          <Euro className="h-4 w-4" />
                          CA annuel (€)
                        </Label>
                        <Input
                          id="ca_magasin"
                          name="ca_magasin"
                          type="number"
                          min="0"
                          value={formData.ca_magasin}
                          onChange={handleChange}
                          placeholder="150000"
                          data-testid="profile-ca-input"
                        />
                      </div>

                      {/* Jeu libre en magasin — pleine largeur, propre ligne */}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="jeu_libre">Jeu libre en magasin</Label>
                      <p className="text-xs text-muted-foreground -mt-1">Le magasin permet-il de jouer librement sur place ?</p>
                      <Select
                        key={`jeu_libre_${formData.jeu_libre === true ? 'T' : formData.jeu_libre === false ? 'F' : 'U'}`}
                        defaultValue={formData.jeu_libre === true ? 'true' : formData.jeu_libre === false ? 'false' : '__unset__'}
                        onValueChange={(v) => {
                          const mapped = v === 'true' ? true : v === 'false' ? false : null;
                          if (mapped !== formData.jeu_libre) {
                            handleChange({ target: { name: 'jeu_libre', value: mapped }});
                          }
                        }}
                      >
                        <SelectTrigger data-testid="profile-jeu-libre-select">
                          <SelectValue placeholder="Non renseigné" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__unset__">Non renseigné</SelectItem>
                          <SelectItem value="true">Oui</SelectItem>
                          <SelectItem value="false">Non</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Animations et événements + Capacité d'accueil — côte à côte */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="animations_magasin">Animations et événements</Label>
                        <p className="text-xs text-muted-foreground -mt-1">Le magasin organise-t-il des animations ou événements ?</p>
                        <Select
                          key={`animations_${formData.animations_magasin === true ? 'T' : formData.animations_magasin === false ? 'F' : 'U'}`}
                          defaultValue={formData.animations_magasin === true ? 'true' : formData.animations_magasin === false ? 'false' : '__unset__'}
                          onValueChange={(v) => {
                            const mapped = v === 'true' ? true : v === 'false' ? false : null;
                            if (mapped !== formData.animations_magasin) {
                              handleChange({ target: { name: 'animations_magasin', value: mapped }});
                            }
                          }}
                        >
                          <SelectTrigger data-testid="profile-animations-select">
                            <SelectValue placeholder="Non renseigné" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__unset__">Non renseigné</SelectItem>
                            <SelectItem value="true">Oui</SelectItem>
                            <SelectItem value="false">Non</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="capacite_accueil">Capacité d'accueil</Label>
                        <Input
                          id="capacite_accueil"
                          name="capacite_accueil"
                          type="number"
                          min="0"
                          value={formData.capacite_accueil}
                          onChange={handleChange}
                          placeholder="20"
                          data-testid="profile-capacite-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Places pour animations/événements
                        </p>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    </div>

                    {/* Site e-commerce */}
                    <div className="mt-4 space-y-2">
                      <Label className="flex items-center gap-2">
                        <Globe className="h-4 w-4" />
                        Site e-commerce
                      </Label>
                      <Select
                        value={boolToSelectValue(formData.site_ecommerce)}
                        onValueChange={(value) => handleBooleanChange('site_ecommerce', value)}
                      >
                        <SelectTrigger className="w-[200px]" data-testid="profile-ecommerce-select">
                          <SelectValue placeholder="Non renseigné" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Oui</SelectItem>
                          <SelectItem value="false">Non</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground">
                        Disposez-vous d'une boutique en ligne ?
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Section: Informations partenaire - UNIQUEMENT POUR PARTENAIRES */}
              {user?.role === 'partenaire' && user?.partner_id && (
              <Card className="border-amber-200 bg-amber-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-amber-900">
                    <Building2 className="h-5 w-5" />
                    Compte Partenaire
                  </CardTitle>
                  <CardDescription className="text-amber-700">
                    Votre rattachement partenaire
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <span className="font-medium text-amber-900">ID Partenaire:</span>
                      <code className="bg-amber-100 px-2 py-1 rounded text-amber-800">
                        {user.partner_id}
                      </code>
                    </div>
                    <p className="text-xs text-amber-700">
                      Cet identifiant relie votre compte aux produits et offres de votre structure.
                    </p>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Section: Préférences - UNIQUEMENT POUR RETAILERS */}
              {user?.role !== 'partenaire' && (
              <Card>
                <CardHeader>
                  <CardTitle>Préférences</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="newsletter"
                      checked={formData.newsletter}
                      onCheckedChange={(checked) => { setFormData({ ...formData, newsletter: checked }); if (originalFormData && originalFormData.newsletter !== checked) _setIsDirty(true); }}
                      data-testid="profile-newsletter-checkbox"
                    />
                    <Label htmlFor="newsletter" className="text-sm font-normal cursor-pointer">
                      Je souhaite recevoir la newsletter
                    </Label>
                  </div>
                </CardContent>
              </Card>
              )}

              {/* Bouton de soumission */}
              <Button 
                type="submit" 
                className="w-full" 
                disabled={loading} 
                data-testid="profile-submit-button"
              >
                {loading ? 'Enregistrement...' : 'Enregistrer les modifications'}
              </Button>
            </form>
          </TabsContent>


          {/* === ONGLET: MES COMMANDES === */}
          <TabsContent value="orders">
            <OrdersContent />
          </TabsContent>

          {/* === ONGLET 2: FAVORIS (TABLEAU + LISTES) === */}
          <TabsContent value="favorites">
            <Card>
              <CardHeader>
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Heart className="h-5 w-5 text-red-500" />
                      Ma Wishlist
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {selectedListId 
                        ? `${filteredFavorites.length} produit(s) dans cette liste`
                        : `${favorites.length} produit(s) au total`
                      }
                    </CardDescription>
                  </div>
                  
                  {/* Gestionnaire de listes */}
                  <FavoriteListsManager
                    lists={favoriteLists}
                    selectedListId={selectedListId}
                    onListSelect={setSelectedListId}
                    onListsChange={handleListsChange}
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                {loadingData ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </div>
                ) : (
                  <FavoritesProductsTable
                    favorites={filteredFavorites}
                    allLists={favoriteLists}
                    loading={loadingData}
                    onCommentUpdate={handleFavoriteCommentUpdate}
                    onRemoveFavorite={handleRemoveFavorite}
                    onListsUpdate={handleFavoriteListsUpdate}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === ONGLET 3: PRODUITS À SUIVRE (TABLEAU) === */}
          <TabsContent value="watchlist">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Flag className="h-5 w-5 text-blue-500" />
                  Produits à suivre
                </CardTitle>
                <CardDescription>
                  Restez informé des mises à jour ({watchlist.length})
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                {loadingData ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </div>
                ) : (
                  <FollowingProductsTable 
                    products={watchlist} 
                    loading={loadingData}
                    onCommentUpdate={handleFollowingCommentUpdate}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* === ONGLET 4: AVIS ET COMMENTAIRES (TABLEAU) === */}
          <TabsContent value="comments">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-green-500" />
                  Mes Avis
                </CardTitle>
                <CardDescription>
                  Vos commentaires sur les produits ({comments.length})
                </CardDescription>
              </CardHeader>
              <CardContent className="p-0 sm:p-6">
                {loadingData ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                  </div>
                ) : (
                  <ReviewsProductsTable
                    reviews={comments}
                    loading={loadingData}
                  />
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
      <NavigationBlockerDialog blocker={blocker} />
    </DashboardWrapper>
  );
};

export default ProfilePage;
